import { SettingsActionDispatcher } from '../../reducer/settings-tree.reducer';
import { DynamicForm, EntityForm } from '../form.types';
import * as _ from 'lodash';
import { SettingsFormState } from '../../reducer/settings-tree-reducer.types';

/**
 * Intercepts and modifies the given form object by merging the current form state
 * into the form's default values and wrapping the onSubmit handler to
 * extract modified values before calling the original onSubmit handler.
 *
 * @param {EditorForm | null} entityForm - The form object to be intercepted and modified.
 * @param {Record<string, any> | undefined} currentFormState - The current state of the form to be merged into the form's default values.
 * @returns {EditorForm | null} - The modified form object or null if the input form is null.
 */
export function interceptForm<Tin = Record<string, any>, Tout = Record<string, any>>(
  nodeId: string,
  entityForm: EntityForm<Tin, Tout> | null,
  currentFormState: SettingsFormState | null,
  extractModifiedValuesOnly: boolean,
  dispatch: SettingsActionDispatcher
): DynamicForm | null {
  if (!entityForm) {
    return null;
  }

  const modifiedValues = extractModifiedValuesOnly
    ? extractModifiedValues(
        entityForm.initialFormOutput as Record<string, any>,
        entityForm.getOutput((currentFormState?.form || {}) as Tin) as Record<string, any>
      )
    : (entityForm.getOutput((currentFormState?.form || {}) as Tin) as Record<string, any>);

  const isCreateForm = currentFormState?.formType === 'create';
  const requiredFields = (entityForm.formSchema.required as string[] | undefined) ?? [];
  const currentForm = (currentFormState?.form ?? {}) as Record<string, any>;
  const allRequiredFieldsFilled =
    requiredFields.length > 0 &&
    requiredFields.every((field) => {
      const val = currentForm[field];
      return val !== null && val !== undefined && val !== '';
    });
  const enabledWithoutTouch = isCreateForm && allRequiredFieldsFilled;
  const hasChanged =
    enabledWithoutTouch || (currentFormState?.isTouched === true && Object.keys(modifiedValues).length > 0);

  // Pull out the entity-specific onReset so we can invoke it alongside RESET_FORM — otherwise
  // it would just be spread into the returned DynamicForm and clobber the reducer dispatch.
  const {
    componentToRenderBelowForm,
    customValidate,
    renderInsideCard,
    onReset: entityOnReset,
    onSubmissionAction,
    ...entityFormRest
  } = entityForm;

  return {
    ...entityFormRest,
    componentToRenderBelowForm: componentToRenderBelowForm as DynamicForm['componentToRenderBelowForm'],
    customValidate: customValidate as DynamicForm['customValidate'],
    renderInsideCard: renderInsideCard as DynamicForm['renderInsideCard'],
    defaultValues: {
      ...(entityForm.defaultValues as Record<string, any>),
      ...currentFormState?.form
    },
    hasChanged,
    onReset() {
      entityOnReset?.();
      dispatch({ type: 'RESET_FORM', payload: { nodeId } });
    },
    onAcknowledgeSuccess: () => {
      dispatch({ type: 'ACKNOWLEDGE_FORM_SUCCESS', payload: { nodeId } });
    },
    onChange(formData) {
      let next = formData as Tin;
      if (entityForm.onChangeInterceptor) {
        next = entityForm.onChangeInterceptor((currentFormState?.form || {}) as Tin, next);
      }
      dispatch({ type: 'UPDATE_FORM', payload: { nodeId, form: next as Record<string, any> } });
    },
    onSubmit: async (formData: Record<string, any>) => {
      const output = entityForm.getOutput(formData as Tin);
      const modifiedValues = extractModifiedValuesOnly
        ? extractModifiedValues(entityForm.initialFormOutput as Record<string, any>, output as Record<string, any>)
        : (output as Record<string, any>);
      dispatch({ type: 'SUBMIT_FORM', payload: { nodeId } });
      try {
        const result = await entityForm.onSubmit(modifiedValues as Tout);
        // If the form submission was successful, the submit handler might have returned a node ID to be selected.
        // This is useful when creating a new node. We enqueue the node selection so that the tree can be updated
        // after the form submission is complete.
        if (result) {
          dispatch({
            type: 'ENQUEUE_NODE_SELECTION',
            payload: {
              entityId: result.entityId,
              parentNodeId: result.parentNodeId,
              requestType: currentFormState!.requestType
            }
          });
        }
        // Run the submission action on successful completion
        onSubmissionAction?.(dispatch, result);
        dispatch({ type: 'SUBMIT_FORM_SUCCESS', payload: { nodeId } });
      } catch (error) {
        dispatch({ type: 'SUBMIT_FORM_ERROR', payload: { nodeId, error } });
      }
    }
  };
}

function extractModifiedValues(initialForm: Record<string, any>, form: Record<string, any>) {
  const modifiedValues: Record<string, any> = {};
  for (const key in initialForm) {
    // Important: we use lodash's isEqual to compare the values because the form output may contain values such as arrays.
    if (!_.isEqual(form[key], initialForm[key])) {
      // If the initial value is defined and the new value is empty, we should set the value to null so
      // that the backend can interpret it as a removal of the value.
      if (!_.isEmpty(initialForm[key]) && _.isEmpty(form[key])) {
        modifiedValues[key] = null;
      } else {
        modifiedValues[key] = form[key];
      }
    }
  }
  for (const key in form) {
    // Important: we use lodash's isEqual to compare the values because the form output may contain values such as arrays.
    if (!_.isEqual(form[key], initialForm[key])) {
      modifiedValues[key] = form[key];
    }
  }
  return modifiedValues;
}
